// Shared helpers for kernel render primitives. Anything used by more
// than one primitive lives here so the per-kind files stay focused.

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { asNumber, formatCompactValue, formatValue, toneColor } from "../../lib/kernelFormat.js";
import { STIPPLE_SIZE, STIPPLE_DOT_RADIUS } from "../../lib/stipple.js";
import { useLabels, scopeStepLabel } from "../../lib/dimensionDefs.js";
import HoverTooltip from "../HoverTooltip.jsx";
import { TOOLTIP_LAYER } from "../../lib/tooltipLayer.js";

export function KernelEmpty({ message }) {
    return (
        <div className="rounded-[2px] border border-dashed border-[color:var(--line)] px-[10px] py-[8px] text-[11px] text-[color:var(--muted-soft)]">
            {message}
        </div>
    );
}

// Warm the shared dimension-label cache for every linked id in `rows`. A
// render spec's `link: {dim, idField}` is the signal that a field holds a
// dim id (endpoint, cluster, …) rather than a display string; warming here
// lets the label swap below resolve to hostnames/names as the cache fills.
// Call unconditionally (it always calls useLabels, so hook order is stable) —
// a null/absent link warms nothing.
export function useLinkLabels(rows, link) {
    const steps = [];
    if (link?.dim && link.idField != null && Array.isArray(rows)) {
        const seen = new Set();
        for (const row of rows) {
            const id = row?.[link.idField];
            if (id == null) continue;
            const key = String(id);
            if (seen.has(key)) continue;
            seen.add(key);
            steps.push({ dim: link.dim, id: key });
        }
    }
    useLabels(steps);
}

// Resolve the display label for a linked row: when the row carries a
// link.dim id, return its cached dim label (falling back to the raw id
// until the cache fills — same behavior table cells have); otherwise
// return `fallback`. Mirrors table.jsx so every primitive labels alike.
export function linkLabel(link, row, fallback, unresolvedText) {
    if (link?.labelField != null) {
        const explicit = row?.[link.labelField];
        if (explicit != null && String(explicit).trim() !== "") return fallback;
    }
    if (link?.dim && link.idField != null) {
        const id = row?.[link.idField];
        if (id != null) {
            const resolved = scopeStepLabel({ dim: link.dim, id: String(id) });
            if (String(resolved) === String(id) && typeof unresolvedText === "string") {
                return unresolvedText;
            }
            return resolved;
        }
    }
    return fallback;
}

// Label text for a dimension value. Storage keeps unattributed rows as the
// empty string on purpose (the analytics examples say "not 'unknown'"), so
// the placeholder belongs here at the render edge, not in the data. Callers
// keep using the raw value for focus/filter identity — only the display
// changes. Without it, a bars kernel grouped by a sparsely-stamped dimension
// draws its largest bucket with no label at all.
export const EMPTY_DIMENSION_LABEL = "Unattributed";
export function dimensionLabel(value, emptyLabel) {
    const fallback = typeof emptyLabel === "string" && emptyLabel.trim() !== ""
        ? emptyLabel
        : EMPTY_DIMENSION_LABEL;
    if (value == null) return fallback;
    const text = formatValue(value, "text");
    return text.trim() === "" ? fallback : text;
}

// The big glanceable number in a KPI / stat tile. Renders the scale-aware
// compact form (1.2K, 8.1M) so wide token-scale counts stay on one line;
// when that abbreviates the value, the exact figure surfaces on hover.
// `className` carries the per-tile type sizing; `tone` is a kernelFormat
// tone name resolved to a color.
export function HeroNumber({ value, format, className, tone }) {
    const display = formatCompactValue(value, format);
    const exact = formatValue(value, format);
    const span = (
        <span className={className} style={tone ? { color: toneColor(tone) } : undefined}>
            {display}
        </span>
    );
    if (display === exact) return span;
    return (
        <HoverTooltip label={exact} placement="top">
            {span}
        </HoverTooltip>
    );
}

export function pickField(data, field) {
    if (data == null) return null;
    // A scalar stat pointed straight at an analytics step receives the rows
    // array (extractRenderData returns `rows`, not the first row). Read from the
    // first row, since `array[field]` is undefined — a blank tile on a correct,
    // non-empty result. Kernels that route through a pick-first-row step already
    // hand us the object; this covers the ones that don't.
    if (Array.isArray(data)) data = data.length ? data[0] : null;
    if (data == null) return null;
    if (!field) return typeof data === "object" && !Array.isArray(data) ? data.value ?? data : data;
    if (typeof data === "object") return data[field];
    return data;
}

export function extractSeries(data, field) {
    if (data == null) return [];
    if (Array.isArray(data)) {
        if (!data.length) return [];
        if (typeof data[0] === "number") return data.map((v) => Number(v) || 0);
        if (field) return data.map((r) => asNumber(r?.[field]) ?? 0);
        return data.map((r) => {
            for (const key of Object.keys(r || {})) {
                const n = asNumber(r[key]);
                if (n != null) return n;
            }
            return 0;
        });
    }
    if (typeof data === "object") {
        const arr = data.series || data.values || [];
        return Array.isArray(arr) ? arr.map((v) => Number(v) || 0) : [];
    }
    return [];
}

// Default layer palette for stacked / multi-series charts. Mirrors what
// ChatConsole uses for render_chart so charts surfaced through either
// tool look identical.
export const DEFAULT_LAYER_COLORS = [
    "var(--ink)",
    "var(--green)",
    "var(--muted)",
    "rgba(72, 66, 60, 0.55)",
    "rgba(72, 66, 60, 0.35)",
];

// Stable, hash-based palette for "n discrete categories" colorings
// (heatmap series, scatter series, sankey nodes). Uses CSS tokens
// where possible so themes still apply.
export const CATEGORY_PALETTE = [
    "var(--green)",
    "var(--ink)",
    "rgba(176, 122, 84, 0.85)",   // amber-ish accent
    "rgba(106, 138, 168, 0.85)",  // muted blue
    "rgba(150, 96, 130, 0.85)",   // muted plum
    "rgba(86, 130, 100, 0.85)",   // muted moss
    "rgba(200, 156, 96, 0.85)",   // sand
    "rgba(120, 120, 140, 0.85)",  // grey
];

export function colorForKey(key, palette = CATEGORY_PALETTE) {
    if (!key) return palette[0];
    let h = 0;
    const s = String(key);
    for (let i = 0; i < s.length; i++) {
        h = (h * 31 + s.charCodeAt(i)) | 0;
    }
    return palette[Math.abs(h) % palette.length];
}

// Resolve a CSS color token (e.g. "var(--green)") to its computed
// rgb/rgba string for canvas drawing. Falls back to the raw value if
// it isn't a var(...) expression. Canvas can't read CSS variables —
// it works on actual colors — so primitives that draw to canvas need
// to resolve once per render.
export function resolveCssColor(node, value) {
    if (!value || typeof value !== "string") return value;
    if (!value.startsWith("var(")) return value;
    const match = /^var\((--[\w-]+)(?:\s*,\s*(.+))?\)$/.exec(value);
    if (!match) return value;
    const [, varName, fallback] = match;
    if (!node) return fallback || "#000";
    try {
        const v = getComputedStyle(node).getPropertyValue(varName).trim();
        return v || fallback || "#000";
    } catch {
        return fallback || "#000";
    }
}

// Hook: track an element's size via ResizeObserver. Used by canvas
// primitives that need to redraw on layout changes (no per-instance
// width prop required).
export function useElementSize(ref) {
    const [size, setSize] = useState({ width: 0, height: 0 });
    useEffect(() => {
        const node = ref.current;
        if (!node) return;
        const update = () => {
            const rect = node.getBoundingClientRect();
            setSize({ width: rect.width, height: rect.height });
        };
        update();
        let ro;
        if (typeof ResizeObserver !== "undefined") {
            ro = new ResizeObserver(update);
            ro.observe(node);
        }
        window.addEventListener("resize", update);
        return () => {
            ro?.disconnect();
            window.removeEventListener("resize", update);
        };
    }, [ref]);
    return size;
}

// Lightweight canvas wrapper: a DPR-aware canvas that calls `draw(ctx,
// {width, height})` whenever its size or `deps` change. Primitives use
// this to keep their canvas drawing concentrated in one callback.
//
// `tooltipFn(x, y)` (optional) turns on hover tooltips: called with the
// pointer position in canvas CSS pixels, it returns null or
// {key, rect: {x, y, w, h}, content} — `rect` is the hovered region in the
// same coordinates, `key` gates re-renders across mousemove ticks, and
// `content` is the tooltip body. The tooltip floats in a body portal
// anchored at the region's top-left corner (not the cursor), sitting where
// an in-cell label plate would — the treemap uses it as the stand-in for
// plates its small cells couldn't fit.
export function CanvasSurface({ draw, deps = [], height, className, title, tooltipFn }) {
    const wrapperRef = useRef(null);
    const canvasRef = useRef(null);
    const size = useElementSize(wrapperRef);
    const [tooltip, setTooltip] = useState(null);
    const lastTipKeyRef = useRef("");

    useEffect(() => {
        const canvas = canvasRef.current;
        const wrapper = wrapperRef.current;
        if (!canvas || !wrapper) return;
        const w = Math.max(0, Math.floor(size.width));
        const h = Math.max(0, Math.floor(size.height));
        if (w === 0 || h === 0) return;
        const dpr = Math.max(1, window.devicePixelRatio || 1);
        if (canvas.width !== Math.round(w * dpr) || canvas.height !== Math.round(h * dpr)) {
            canvas.width = Math.round(w * dpr);
            canvas.height = Math.round(h * dpr);
            canvas.style.width = `${w}px`;
            canvas.style.height = `${h}px`;
        }
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, w, h);
        try {
            draw(ctx, { width: w, height: h, root: wrapper });
        } catch (err) {
            // Don't crash the chat over a malformed primitive — leave
            // the canvas blank so the error stays visible in devtools
            // without taking down siblings.
            if (typeof console !== "undefined") console.error("CanvasSurface draw failed", err);
        }
        // Geometry may have moved under an anchored tooltip — drop it and
        // let the next mouse move re-anchor at the new spot.
        lastTipKeyRef.current = "";
        setTooltip((t) => (t ? null : t));
        // We intentionally include `draw` here. The primitive components
        // pass a useCallback-stabilized draw fn or accept the re-runs;
        // their deps list controls cache invalidation upstream.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [size.width, size.height, ...deps]);

    // Gate setTooltip on hit-key transitions: mousemove fires many times
    // per frame, and without the gate every tick would re-render the
    // portal even when the hovered region hasn't changed (same rationale
    // as DotGrid's lastHitKeyRef).
    const handleMouseMove = (event) => {
        const canvas = canvasRef.current;
        if (!canvas || !tooltipFn) return;
        const rect = canvas.getBoundingClientRect();
        const hit = tooltipFn(event.clientX - rect.left, event.clientY - rect.top);
        const key = hit ? hit.key : "";
        if (key === lastTipKeyRef.current) return;
        lastTipKeyRef.current = key;
        if (!hit) {
            setTooltip(null);
            return;
        }
        // Anchor at the region's top-left, inset like the treemap's drawn
        // label plates so the tooltip reads as the plate the region was too
        // small to fit. Flip per-axis against the CANVAS edges (not the
        // viewport): near the right edge grow leftward from the region's
        // top-right corner, near the bottom grow upward from its bottom —
        // the tooltip stays over the drawing, like the plates it stands in
        // for, instead of spilling out of the tile.
        const TOOLTIP_W = 240;
        const TOOLTIP_H = 48;
        const inset = 4;
        const rightAnchor = hit.rect.x + inset + TOOLTIP_W > rect.width;
        const bottomAnchor = hit.rect.y + inset + TOOLTIP_H > rect.height;
        setTooltip({
            left: rect.left + (rightAnchor ? hit.rect.x + hit.rect.w - inset : hit.rect.x + inset),
            top: rect.top + (bottomAnchor ? hit.rect.y + hit.rect.h - inset : hit.rect.y + inset),
            rightAnchor,
            bottomAnchor,
            content: hit.content,
        });
    };

    const handleMouseLeave = () => {
        if (lastTipKeyRef.current === "") return;
        lastTipKeyRef.current = "";
        setTooltip(null);
    };

    return (
        <>
            <div
                ref={wrapperRef}
                className={className}
                style={height != null ? { height: `${height}px` } : undefined}
                title={title}
                onMouseMove={tooltipFn ? handleMouseMove : undefined}
                onMouseLeave={tooltipFn ? handleMouseLeave : undefined}
            >
                <canvas ref={canvasRef} style={{ display: "block", width: "100%", height: "100%" }} />
            </div>
            {tooltip ? createPortal(
                <div
                    role="tooltip"
                    className={`pointer-events-none fixed ${TOOLTIP_LAYER} rounded-[8px] border border-[color:var(--line-strong)] bg-[color:var(--bg)] px-[10px] py-[8px] text-[11px] leading-[1.35] text-[color:var(--ink)] shadow-[0_4px_12px_rgba(0,0,0,0.25)]`}
                    style={{
                        top: `${tooltip.top}px`,
                        left: `${tooltip.left}px`,
                        maxWidth: "260px",
                        // Anchor flips pull the box left of / above its anchor
                        // so it grows toward the canvas edge it has room for.
                        transform: [
                            tooltip.rightAnchor ? "translateX(-100%)" : null,
                            tooltip.bottomAnchor ? "translateY(-100%)" : null,
                        ].filter(Boolean).join(" ") || undefined,
                    }}
                >
                    {tooltip.content}
                </div>,
                document.body,
            ) : null}
        </>
    );
}

// Stipple-fill a rectangular region with a dot grid matching the
// legacy SurfaceChart aesthetic (renderers.js → fillDotsInPath). All
// canvas-backed primitives use this for area fills so they share the
// "Tufte-style" texture instead of flat alpha washes.
//
//   size     — pattern pitch in px (default 5, matching SurfaceChart's
//              SPARKLINE preset; the kernel primitives' surfaces are
//              uniformly smaller than the main time-series chart so the
//              denser pattern reads correctly on heatmap cells, ribbons,
//              and funnel bars without looking like sparse confetti)
//   dotRadius — dot radius (default 0.8, matches sparkline)
//   opacity  — dot alpha (default 0.55, matches SurfaceChart fill)
//   density  — 0..1 multiplier on `opacity`. Used to encode intensity:
//              pass `t` from share-of-max so dim regions show fainter
//              dots without losing the texture.
export function fillStippleRect(ctx, x, y, w, h, color, opts = {}) {
    if (w <= 0 || h <= 0) return;
    const {
        size = STIPPLE_SIZE,
        dotX = 1,
        dotY = 1,
        dotRadius = STIPPLE_DOT_RADIUS,
        opacity = 0.65,
        density = 1,
    } = opts;
    ctx.save();
    ctx.beginPath();
    ctx.rect(x, y, w, h);
    ctx.clip();
    ctx.fillStyle = color;
    ctx.globalAlpha = Math.max(0, Math.min(1, opacity * density));
    // Anchor the grid to integer multiples of `size` so neighbouring
    // cells share the same dot positions — the pattern looks continuous
    // across cell borders instead of seam-breaking at every edge.
    const startY = Math.floor(y / size) * size + dotY;
    const startX = Math.floor(x / size) * size + dotX;
    for (let py = startY; py < y + h; py += size) {
        if (py < y) continue;
        for (let px = startX; px < x + w; px += size) {
            if (px < x) continue;
            ctx.beginPath();
            ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    ctx.restore();
}

// Stipple-fill an arbitrary path. Caller is responsible for tracing
// the path on `ctx` before calling — we clip to it and dot-fill the
// AABB. `bounds` is {left, top, right, bottom} of the path's bbox.
export function fillStipplePath(ctx, bounds, color, opts = {}) {
    const {
        size = STIPPLE_SIZE,
        dotX = 1,
        dotY = 1,
        dotRadius = STIPPLE_DOT_RADIUS,
        opacity = 0.65,
        density = 1,
    } = opts;
    ctx.save();
    ctx.clip();
    ctx.fillStyle = color;
    ctx.globalAlpha = Math.max(0, Math.min(1, opacity * density));
    const startY = Math.floor(bounds.top / size) * size + dotY;
    const startX = Math.floor(bounds.left / size) * size + dotX;
    for (let py = startY; py < bounds.bottom; py += size) {
        if (py < bounds.top) continue;
        for (let px = startX; px < bounds.right; px += size) {
            if (px < bounds.left) continue;
            ctx.beginPath();
            ctx.arc(px, py, dotRadius, 0, Math.PI * 2);
            ctx.fill();
        }
    }
    ctx.restore();
}

// Linear interpolation between two hex/rgb strings — for share-of-max
// heatmap cells, treemap shading, etc. Both colors are resolved to
// {r,g,b,a} once per cell.
export function lerpColor(a, b, t) {
    const A = parseColor(a);
    const B = parseColor(b);
    if (!A || !B) return a;
    const u = Math.max(0, Math.min(1, t));
    const r = Math.round(A.r + (B.r - A.r) * u);
    const g = Math.round(A.g + (B.g - A.g) * u);
    const bl = Math.round(A.b + (B.b - A.b) * u);
    const al = A.a + (B.a - A.a) * u;
    return `rgba(${r}, ${g}, ${bl}, ${al.toFixed(3)})`;
}

function parseColor(c) {
    if (!c || typeof c !== "string") return null;
    const s = c.trim();
    if (s.startsWith("#")) {
        const hex = s.slice(1);
        if (hex.length === 3) {
            const r = parseInt(hex[0] + hex[0], 16);
            const g = parseInt(hex[1] + hex[1], 16);
            const b = parseInt(hex[2] + hex[2], 16);
            return { r, g, b, a: 1 };
        }
        if (hex.length === 6) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            return { r, g, b, a: 1 };
        }
        if (hex.length === 8) {
            const r = parseInt(hex.slice(0, 2), 16);
            const g = parseInt(hex.slice(2, 4), 16);
            const b = parseInt(hex.slice(4, 6), 16);
            const a = parseInt(hex.slice(6, 8), 16) / 255;
            return { r, g, b, a };
        }
    }
    const rgb = /^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)\s*(?:,\s*([\d.]+))?\s*\)$/.exec(s);
    if (rgb) {
        return {
            r: Number(rgb[1]),
            g: Number(rgb[2]),
            b: Number(rgb[3]),
            a: rgb[4] != null ? Number(rgb[4]) : 1,
        };
    }
    return null;
}
